import { ComponentRef, ElementRef, Injectable, Injector, OnDestroy } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import deepmerge from 'deepmerge';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { CumulativeBoxComponent } from './cumulative-box/cumulative-box.component';
import { CumulativeBoxStore } from './cumulative-box/cumulative-box.service';
import {
  CUMULATIVE_BOX_CONTROL,
  CUMULATIVE_BOX_STORE,
  CumulativeBoxOverlayData,
  EDITING_BOX_RECORDS,
  EDITING_BOX_SECTIONS,
  EditingBoxOverlayConfig,
} from './editing-box-overlay.config';
import { EditingBoxComponent } from './editing-box.component';
import { LeaseTermEditBoxComponent } from './lease-term-edit-box/lease-term-box.component';
import { TagsOverlayComponent } from './tags-overlay/tags-overlay.component';

const DEFAULT_OVERLAY_CONFIG = { hasBackdrop: true, backdropClass: 'backdrop-class' };
const DEFAULT_POSITION: ConnectedPosition = {
  originX: 'start',
  originY: 'bottom',
  overlayX: 'start',
  overlayY: 'top',
};

@Injectable({ providedIn: 'root' })
export class EditingBoxService implements OnDestroy {
  /*
   * This will store ALL column field configs (/columns/ response)
   * in a flattened way
   */
  private _cachedColumns = new BehaviorSubject<any>({});
  public cachedColumns$ = this._cachedColumns.asObservable();

  private _afterClosed = new Subject();
  afterClosed$ = this._afterClosed.asObservable().pipe(take(1));

  private overlayRef: OverlayRef;

  constructor(
    public overlay: Overlay,
    private injector: Injector
  ) {}

  ngOnDestroy() {
    this._afterClosed.complete();
    this._cachedColumns.complete();
  }

  /**
   * Get an array of sections with two props updatableFields (string[]) and
   * labelMode(string) every field name in updatableFields will be mapped to a
   * field column config object from _cachedColumns.value
   *
   * @param event with target element
   * @param PropertyInterface with edpUpdateConf, this param should be an array but
   * is flexible enough to receive an object (it will be transformed to array),
   * also can receive a function BUT this fn must return and array of object
   * with { updatableFields: string[], labelMode: string }
   * @param record Record object to update
   */
  public openEditingBoxFromClick({ target }, { leaseTermFields, edpUpdateConf }: any, record, field?) {
    if (edpUpdateConf) {
      if (typeof edpUpdateConf === 'function') {
        edpUpdateConf = edpUpdateConf(record);
      }
      if (!Array.isArray(edpUpdateConf)) {
        edpUpdateConf = [edpUpdateConf];
      }
      const mappedConfigs = edpUpdateConf
        .map(conf => {
          const { updatableFields, labelMode = 'normal' } = conf;
          let columns = updatableFields;
          if (typeof updatableFields === 'function') {
            columns = updatableFields(record);
          }
          columns = columns.map(field => this._cachedColumns.value[field]).filter(field => !!field);
          return { labelMode, columns };
        })
        .filter(section => Array.isArray(section.columns) && section.columns.length > 0);

      if (mappedConfigs.length > 0) {
        // Expose the control in case needed
        return this.openBox({
          origin: target,
          record,
          sections: mappedConfigs,
          leaseTermFields,
          field,
        });
      }
    }
  }

  public openBox({ origin, record, sections, leaseTermFields, field }) {
    const editBoxComponent = field?.includes('user_tag/')
      ? TagsOverlayComponent
      : leaseTermFields
        ? LeaseTermEditBoxComponent
        : EditingBoxComponent;
    const positionStrategy = this.overlay.position().flexibleConnectedTo(origin).withPositions([DEFAULT_POSITION]);

    const config = new EditingBoxOverlayConfig(
      {
        ...DEFAULT_OVERLAY_CONFIG,
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.close(),
      },
      record,
      sections
    );
    this.overlayRef = this.overlay.create(config);
    this.overlayRef.backdropClick().pipe(take(1)).subscribe(this.disposeBox.bind(this));

    const injector = this.makeEditingBoxInjectors(config);
    this.attachDialogContainer<EditingBoxComponent>(
      editBoxComponent as ComponentType<EditingBoxComponent>,
      this.overlayRef,
      injector
    );

    return this.overlayRef;
  }

  /**
   * Makes injectors for EditingBoxComponent
   * @param param0
   */
  private makeEditingBoxInjectors({ record, sections }: EditingBoxOverlayConfig): Injector {
    return Injector.create({
      parent: this.injector,
      // Set custom injection tokens
      providers: [
        {
          provide: EDITING_BOX_SECTIONS,
          useValue: sections,
        },
        {
          provide: EDITING_BOX_RECORDS,
          useValue: record,
        },
      ],
    });
  }

  /**
   * Opens portal with cumulative service and no backdrop
   * @param origin
   * @param cumulativeOverlayData
   * @param position
   */
  public openCumulativeBox(
    origin: ElementRef | HTMLElement,
    cumulativeOverlayData: CumulativeBoxOverlayData,
    position?: Partial<ConnectedPosition>
  ) {
    position = deepmerge(DEFAULT_POSITION, position || {});
    const { record, totalColumn, cumulativeColumns, autoSum, showAutoSumControl, isNewRecord, customTemplateRefMap } =
      cumulativeOverlayData;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions([position as ConnectedPosition]);

    const config = new OverlayConfig({
      ...DEFAULT_OVERLAY_CONFIG,
      hasBackdrop: true,
      disposeOnNavigation: true,
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    this.overlayRef = this.overlay.create(config);

    const store = new CumulativeBoxStore(
      record,
      totalColumn,
      cumulativeColumns,
      isNewRecord,
      showAutoSumControl,
      autoSum,
      customTemplateRefMap
    );
    const injector = this.makeCumulativeBoxInjectors(store);

    this.attachDialogContainer<CumulativeBoxComponent>(
      CumulativeBoxComponent as ComponentType<CumulativeBoxComponent>,
      this.overlayRef,
      injector
    );
    return { afterClosed: () => this.afterClosed$, store };
  }

  /**
   * * Makes injectors for CumulativeBoxComponent
   * @param param0
   */
  private makeCumulativeBoxInjectors(store: CumulativeBoxStore): Injector {
    // Instantiate new Injector
    return Injector.create({
      parent: this.injector,
      providers: [
        { provide: CUMULATIVE_BOX_STORE, useValue: store },
        {
          provide: CUMULATIVE_BOX_CONTROL,
          useValue: {
            close: (data: CumulativeBoxStore) => {
              const changes = !!data ? data.cumulativeColumns : null;
              this.disposeBox(changes);
              store.destroy();
            },
          },
        },
      ],
    });
  }

  /**
   * Returns an object with a copy of this.disposeBox (remote control) to be passed
   * to the projected portal so it can close by itself. also sends out the captured
   * data in the component if data is passed (similar to MatDialog.close()
   * OR MatDialog.close(data))
   */
  private makeControl() {
    return { close: this.disposeBox.bind(this) };
  }

  /**
   * Creates a new ComponentPortal based in the passed Component argument and injectors
   * and return the instance of the attached component to the overlay
   * @param Component
   * @param overlayRef
   * @param injector
   */
  private attachDialogContainer<T>(Component: ComponentType<T>, overlayRef: OverlayRef, injector: Injector) {
    const containerPortal = new ComponentPortal<T>(Component, null, injector);
    const containerRef: ComponentRef<T> = overlayRef.attach(containerPortal);
    return containerRef.instance;
  }

  /**
   * Method to dispose the portal, it notifies to afterClosed subscribers
   * and passed data obtained from projected control. It also cleans the overlay
   * by detaching the component and other utils from it
   *
   * @param data
   */
  public disposeBox(data?: any) {
    // notify closing
    this._afterClosed.next(data);
    // clean
    this.overlayRef.detach();
    this.overlayRef.detachBackdrop();
    this.overlayRef.dispose();
    this.overlayRef = null;
  }

  public setFlatColumnsConfigs(columns) {
    this._cachedColumns.next(columns);
  }

  public getFlatColumnsConfigs() {
    return this._cachedColumns.value;
  }
}
